import { useState } from 'react';
import { Button, Dialog, DropdownMenu, Select, Textarea } from '@artosera/ui';
import { ETAPES_AFFAIRE, MOTIFS_PERTE, type EtapeAffaire } from '@artosera/shared';
import { useTranslation } from 'react-i18next';
import {
  pipelineApi,
  useActionPipeline,
  type AffairePresentee,
  type MotifPerte,
} from '../../api/pipeline.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';

export function MenuDeplacement({
  affaire,
  onDeplacer,
  disabled,
}: {
  affaire: AffairePresentee;
  onDeplacer: (affaire: AffairePresentee, vers: EtapeAffaire) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('regie');
  return (
    <DropdownMenu
      trigger={
        <Button
          variant="ghost"
          disabled={disabled}
          aria-label={t('pipeline.deplacerAffaire', { numero: affaire.numero })}
        >
          {t('pipeline.deplacer')}
        </Button>
      }
      entries={ETAPES_AFFAIRE.map((etape) => ({
        type: 'item',
        item: {
          key: etape,
          label: t(`pipeline.etapes.${etape}`),
          disabled: etape === affaire.etape,
          onSelect: () => onDeplacer(affaire, etape),
        },
      }))}
    />
  );
}
export function PerteDialog({
  affaire,
  onFermer,
}: {
  affaire: AffairePresentee;
  onFermer: () => void;
}) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const [code, setCode] = useState<MotifPerte['code'] | ''>('');
  const [texte, setTexte] = useState('');
  const mutation = useActionPipeline(galerieSlug, (motifPerte: MotifPerte, appel) =>
    pipelineApi.deplacer(
      galerieSlug,
      affaire.id,
      { vers: 'perdue', version: affaire.version, motifPerte },
      appel,
    ),
  );
  const valide = code !== '' && (code !== 'autre' || texte.trim().length > 0);
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
      title={t('regie:pipeline.marquerPerdue')}
      description={t('regie:pipeline.motifObligatoire')}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <Button
          disabled={!valide || mutation.isError}
          loading={mutation.isPending}
          onClick={() => {
            if (!valide) return;
            void mutation
              .mutateAsync({ code, ...(code === 'autre' ? { texte: texte.trim() } : {}) })
              .then(onFermer)
              .catch(() => {});
          }}
        >
          {t('regie:pipeline.marquerPerdue')}
        </Button>
      }
    >
      <Field htmlFor="affaire-motif" label={t('regie:pipeline.motif')}>
        <Select
          id="affaire-motif"
          value={code}
          onValueChange={(v) => setCode(v as typeof code)}
          options={MOTIFS_PERTE.map((value) => ({
            value,
            label: t(`regie:pipeline.motifs.${value}`),
          }))}
        />
      </Field>
      {code === 'autre' ? (
        <Field htmlFor="affaire-motif-texte" label={t('regie:pipeline.preciserMotif')}>
          <Textarea
            id="affaire-motif-texte"
            required
            maxLength={2000}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
          />
        </Field>
      ) : null}
      {mutation.isError ? (
        <p role="alert">
          {estApiError(mutation.error) && mutation.error.status === 409
            ? t('regie:pipeline.conflit')
            : t('commun:erreurReseau')}
        </p>
      ) : null}
    </Dialog>
  );
}
